import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaMapMarkerAlt, FaPlus } from "react-icons/fa";
import { RxCrossCircled } from "react-icons/rx";
import { useNavigate } from "react-router-dom";
import AddAddressModal from "../Addresses/AddAddressModal";
import MapLocationSelector from "./MapLocationSelector";
import AddressSkeleton from "../Addresses/AddressSkeleton";

const AddressModal = ({ isOpen, onClose, onSelectAddress }) => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isMapSelectorOpen, setIsMapSelectorOpen] = useState(false);

  useEffect(() => {
    const userDataString = localStorage.getItem("BricksNbar-Users-Data");
    if (userDataString) {
      setUserData(JSON.parse(userDataString));
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchUserAddresses();
    }
  }, [isOpen]);

  const fetchUserAddresses = async () => {
    setLoading(true);
    try {
      const userId = userData?._id;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/address/get-user-addresses/${userId}`
      );

      setAddresses(response.data || []);
    } catch (error) {
      console.error("Error fetching addresses:", error);
    } finally {
      setLoading(false);
    }
  };

  const navigateToMapSelection = () => {
    setIsMapSelectorOpen(true);
  };

  const handleSelectAddress = (addressId) => {
    const selectedAddress = addresses.find((addr) => addr._id === addressId);
    onSelectAddress(selectedAddress);
    onClose();
  };

  const handleAddNew = () => {
    setIsAddModalOpen(true);
  };

  const handleSelectMapLocation = async (locationData) => {
    try {
      setLoading(true);

      // First, we need to parse the address components from the Google Maps geocoded address
      const addressParts = parseAddressComponents(locationData.address);

      // Create a new address object with the location data
      const newAddress = {
        userId: userData?._id,
        fullName: userData?.name || "Default Name", // Use user's name or default
        phone: userData?.phone || "", // Use user's phone or empty
        addressType: "Home", // Default address type
        addressLine1: addressParts.street || locationData.address.split(",")[0],
        addressLine2: "",
        city: addressParts.city || "",
        state: addressParts.state || "",
        country: addressParts.country || "India", // Default to India
        postalCode: addressParts.postalCode || "",
        coordinates: {
          latitude: locationData.location.lat,
          longitude: locationData.location.lng,
        },
        isDefault: addresses.length === 0, // Make default if it's the first address
      };

      // Save address to database
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/address/add-address`,
        newAddress
      );

      if (response.data) {
        // Refresh addresses and select the new one
        await fetchUserAddresses();
        onSelectAddress(response.data);
        onClose();
      }
    } catch (error) {
      console.error("Error saving map location as address:", error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to parse address components from a formatted address
  const parseAddressComponents = (formattedAddress) => {
    const components = {};

    // Try to extract postal code
    const postalCodeMatch = formattedAddress.match(/\b\d{6}\b/); // Indian postal codes are 6 digits
    if (postalCodeMatch) {
      components.postalCode = postalCodeMatch[0];
    }

    // Split the address by commas
    const parts = formattedAddress.split(",").map((part) => part.trim());

    // The last part is usually the country
    if (parts.length > 0) {
      components.country = parts[parts.length - 1];
    }

    // The second-to-last part might contain the state or region
    if (parts.length > 1) {
      // Check if it contains postal code and remove it
      let statePart = parts[parts.length - 2];
      if (components.postalCode) {
        statePart = statePart.replace(components.postalCode, "").trim();
      }
      components.state = statePart;
    }

    // The third-to-last part is often the city
    if (parts.length > 2) {
      components.city = parts[parts.length - 3];
    }

    // The first part is usually the street address
    if (parts.length > 0) {
      components.street = parts[0];
    }

    return components;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full max-w-lg mx-auto max-h-[90vh] overflow-hidden">
        <div className="p-6">
          <div className="justify-between flex items-center mb-4">
            <h2 className="text-xl font-bold">Select an address</h2>
            <RxCrossCircled
              className="cursor-pointer"
              size={20}
              onClick={onClose}
            />
          </div>
          {/* <button
            onClick={navigateToMapSelection}
            className="w-full border border-gray-300 rounded-md p-3 mb-3 text-center flex items-center justify-center"
          >
            <FaMapMarkerAlt className="text-red-600 mr-2" />
            <span className="text-red-600 font-medium">
              Choose Address On Map
            </span>
          </button> */}

          <button
            onClick={handleAddNew}
            className="w-full border border-gray-300 rounded-md p-3 mb-4 text-center flex items-center justify-center"
          >
            <FaPlus className="text-red-600 mr-2" size={12} />
            <span className="text-red-600 font-medium">Add New Address</span>
          </button>

          <h3 className="font-bold text-gray-700 mb-1">SAVED ADDRESSES</h3>
          <h3 className="font-bold text-gray-700 mb-3">
            (Choose any address to go ahead..)
          </h3>

          {loading ? (
            <AddressSkeleton />
          ) : addresses.length > 0 ? (
            <div className="overflow-y-auto max-h-96">
              {addresses.map((address) => (
                <div
                  key={address._id}
                  className={`mb-4 border rounded-lg p-4 cursor-pointer hover:border-red-600 ${
                    address.isDefault
                      ? "border-2 border-red-600"
                      : "border-gray-200"
                  }`}
                  onClick={() => handleSelectAddress(address._id)}
                >
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-bold">{address.fullName}</span>
                    <span className="bg-red-700 text-white text-xs font-bold px-2 py-1 rounded">
                      {address.addressType}
                    </span>
                  </div>
                  <p className="text-gray-600">{address.addressLine1}</p>
                  {address.addressLine2 && (
                    <p className="text-gray-600">{address.addressLine2}</p>
                  )}
                  <p className="text-gray-600">
                    {address.city} - {address.postalCode}
                  </p>
                  <p className="text-gray-600">
                    {address.state}, {address.country}
                  </p>
                  <p className="font-medium mt-1">Mobile: {address.phone}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No saved addresses found
            </div>
          )}
        </div>

        {/* Add Address Modal */}
        <AddAddressModal
          isOpen={isAddModalOpen}
          onClose={() => {
            setIsAddModalOpen(false);
            fetchUserAddresses();
          }}
        />

        {/* Map Location Selector */}
        <MapLocationSelector
          isOpen={isMapSelectorOpen}
          onClose={() => setIsMapSelectorOpen(false)}
          onSelectLocation={handleSelectMapLocation}
        />
      </div>
    </div>
  );
};

export default AddressModal;
